import { ReactNode } from 'react'
import { StepByStepFlowStepId } from 'packages/types'
import { ButtonSize, ButtonTheme } from 'builder/components/Button'
import { useI18n } from 'builder/hooks/useI18n'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import Icon24 from 'builder/components/Icon'
import { SectionNames, selectors } from 'builder/modules/resumeEditor'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { StartTailoringButton } from 'builder/components/DocumentEditor/TailoringFromPublicJobBoard'
import { NavigationDots } from '../NavigationDots/NavigationDots'
import { LOCALIZATION_PREFIX } from '../../constants'
import { StepByStepSectionData } from '../../constructSectionsData'
import { ResumeWritingActions, useResumeWriting } from '../ResumeWritingBanner'

import {
  NavigationSectionContainer,
  BackContainer,
  NextContainer,
  ActionContainer,
  NextButton,
  BackButton,
  NavigationBorder,
  BorderTop,
  BorderBottom,
  SkipButton,
} from './styles'

type Props = {
  handleBack?: () => void
  handleNext?: () => void
  handleSkipAIReview?: () => void
  handleAIReview?: () => void
  nextButtonText?: string
  currentIndex: number
  sections?: StepByStepSectionData[]
  isBackButtonPassive?: boolean
  children?: ReactNode
  isNextActionInProgress?: boolean
  currentStep?: StepByStepFlowStepId
  isFinishStep?: boolean
}

export const NavigationSection = (props: Props) => {
  const {
    handleBack,
    handleNext,
    handleSkipAIReview,
    handleAIReview,
    nextButtonText,
    currentIndex,
    sections = [],
    isBackButtonPassive,
    children,
    isNextActionInProgress,
    currentStep,
  } = props
  const { i18n } = useI18n()
  const { isPhone } = useMediaQueries()
  const { isBannerVisible: isResumeWritingBannerVisible } = useResumeWriting()
  const dotsProps = { currentIndex, sections }
  const activeSection = sections[currentIndex]
  const shouldShowBannerActions =
    activeSection?.id === SectionNames.educations && isResumeWritingBannerVisible

  const BannerActions = ResumeWritingActions

  const autoTailoringFromPublicJobBoard = useTypedSelector(
    selectors.autoTailoringFromPublicJobBoard,
  )

  return (
    <NavigationSectionContainer>
      <NavigationBorder>
        <BorderTop />
        <BorderBottom />
      </NavigationBorder>
      {shouldShowBannerActions ? (
        <BannerActions />
      ) : (
        <>
          <ActionContainer>
            <BackContainer>
              {!isBackButtonPassive && (
                <BackButton
                  onClick={handleBack}
                  size={isPhone ? ButtonSize.small : ButtonSize.default}
                  theme={ButtonTheme.ghost}
                >
                  {isPhone ? (
                    <Icon24.ChevronLeft />
                  ) : (
                    i18n.t(`${LOCALIZATION_PREFIX}.navigation_container.back_button_text`)
                  )}
                </BackButton>
              )}

              {!isPhone && !!sections.length && <NavigationDots {...dotsProps} />}
            </BackContainer>
            {!isPhone && children}

            {currentStep === 'aiResumeReview' ? (
              <NextContainer>
                <SkipButton
                  size={isPhone ? ButtonSize.small : ButtonSize.default}
                  onClick={handleSkipAIReview}
                  theme="ghost"
                  data-testid="step-by-step-skip-review-button"
                >
                  {i18n.t(`${LOCALIZATION_PREFIX}.navigation_container.skip`)}
                </SkipButton>
                <NextButton
                  size={isPhone ? ButtonSize.small : ButtonSize.default}
                  onClick={handleAIReview}
                  theme="alternative"
                  leadingIcon={<Icon24.FeaturedRegular />}
                  data-testid="step-by-step-get-review-button"
                >
                  {i18n.t(`${LOCALIZATION_PREFIX}.navigation_container.get_review`)}
                </NextButton>
              </NextContainer>
            ) : (
              <NextContainer>
                <NextButton
                  size={isPhone ? ButtonSize.small : ButtonSize.default}
                  onClick={handleNext}
                  isLoading={isNextActionInProgress}
                  disabled={isNextActionInProgress}
                  data-testid="step-by-step-flow-next-button"
                >
                  {nextButtonText}
                </NextButton>
              </NextContainer>
            )}
            {isPhone && children}
          </ActionContainer>
          {isPhone && !!sections.length && <NavigationDots {...dotsProps} />}
        </>
      )}
      {autoTailoringFromPublicJobBoard.enabled && isPhone && <StartTailoringButton />}
    </NavigationSectionContainer>
  )
}
